import { useMutation, useQuery } from "@tanstack/react-query";
import apiClient from "@apis/index";
import Routes from "./routes";
import {
  DiamondDetail,
  MatchingBandDataResponse,
} from "@src/types/DiamondDetail";
import {
  IncludedCategories,
  ProductRequestParams,
} from "@apis/utils/productUtils";
import { GIACertificateResponse } from "@src/types/GIACertificateResponse";
import { ProductCategory, ProductTypeCommon } from "@utils/constants";
import {
  customParamsSerializer,
  isGemstone,
  isJewelry,
  isLabGrown,
  isLabGrownJewelry,
} from "@utils/product/productValidators";
import { JewelryVariation } from "@src/types/JewelryVariationDetails";
import { AdditionalAttributes } from "@src/types/CartItemListResponse";
import { AddToJewelryResponse } from "@src/types/AddToJewelryOptions";

export interface JewelryComponentsResponse {
  success: boolean;
  components: any[];
}

export const useGetJewelryComponents = (
  productId: number,
  priceMode: number,
  isEnabled: boolean = false
) => {
  return useQuery({
    enabled: isEnabled,
    queryKey: ["jewelry_components", productId, priceMode],
    cacheTime: 0,
    staleTime: 0,
    queryFn: () =>
      apiClient.get<JewelryComponentsResponse>(
        Routes.GET_JEWELRY_COMPONENTS(productId),
        {
          params: {
            "jewelry[jewelry_ids][]": productId,
            price_mode: priceMode,
          },
          headers: {
            ...apiClient.defaults.headers.common,
            "If-None-Match": "",
          },
        }
      ),
  });
};

export const useGetProductById = (
  isEnabled: boolean,
  productType: IncludedCategories,
  productId: number | string,
  userInfo: any,
  priceMode: number,
  priceMarkup: number,
  itemType: ProductCategory,
  mGroupIds?: any,
  isCustomApp?: boolean,
  additionalAttributes?: AdditionalAttributes,
  type?: string
) => {
  const productParams =
    ProductRequestParams[productType as keyof typeof ProductRequestParams];
  const serializeConfig = (config: Record<string, any>) => {
    const result: Record<string, any> = {};
    Object.entries(config).forEach(([key, value]) => {
      if (typeof value === "object" && value !== null) {
        // stringify nested objects
        result[`jewelry[config][${key}]`] = JSON.stringify(value);
      } else {
        result[`jewelry[config][${key}]`] = value;
      }
    });
    return result;
  };

  return useQuery({
    enabled: isEnabled,
    cacheTime: 0,
    staleTime: 0,
    queryKey: [
      "PRODUCT_PAGE",
      productParams?.queryKey,
      productId,
      userInfo.user?.id,
      userInfo.user?.price_mode,
      userInfo.user?.currency,
      userInfo.isGuest,
      priceMode,
      priceMarkup,
    ],
    queryFn: () =>
      apiClient.get<DiamondDetail>(productParams.route, {
        params: {
          [type === "stock"
            ? (productParams.stockNum as string)
            : (productParams.idName as string)]: productId,
          price_mode: priceMode,
          ...(isLabGrown(itemType) && {
            lab_grown: true,
          }),
          ...(isGemstone(itemType) &&
            mGroupIds?.[0] !== 0 && { "gemstone[group_ids]": mGroupIds }),
          ...(isGemstone(itemType) &&
            !isCustomApp && { "gemstone[vdb_setting]": !isCustomApp }),
          ...(isJewelry(itemType) &&
            !isCustomApp && { "jewelry[vdb_setting]": !isCustomApp }),
          ...(isLabGrownJewelry(itemType) &&
            !isCustomApp && {
              "jewelry[sub_type]": ProductTypeCommon.LABGROWN_JEWELRY_SUB_TYPE,
            }),
          ...(isJewelry(itemType) &&
            mGroupIds?.[0] !== 0 && { "jewelry[group_ids]": mGroupIds }),
          ...(isJewelry(itemType) &&
            Object.keys(additionalAttributes || {}).length > 0 && {
              ...serializeConfig(
                additionalAttributes?.config as Record<string, any>
              ),
              "jewelry[personalization]":
                additionalAttributes?.personalization as any,
            }),
        },
        ...(Object.keys(additionalAttributes || {}).length > 0 && {
          paramsSerializer: customParamsSerializer,
        }),
        headers: {
          ...apiClient.defaults.headers.common,
          "If-None-Match": "",
        },
      }),
  });
};

export const useGetJewelryVariation = (
  productId: number,
  priceMode: number,
  pageSize: number,
  isVariationRequired: boolean,
  dynamicParams?: Record<string, string>,
  diamondId?: number,
  userLanguage?: any,
  priceMarkup?: string
) => {
  // Stable query key - stringify params so identical requests share a cache entry
  const dynamicParamsForKey = diamondId
    ? null
    : Object.keys(dynamicParams ?? {}).length > 0
    ? JSON.stringify(dynamicParams)
    : null;

  return useQuery({
    enabled: !!isVariationRequired,
    queryKey: [
      "jewelry_variation",
      productId,
      priceMode,
      pageSize,
      diamondId,
      dynamicParamsForKey,
      userLanguage,
      priceMarkup,
    ],
    cacheTime: 0,
    staleTime: 0,
    queryFn: () =>
      apiClient.get<JewelryVariation>(Routes.GET_JEWELRY_VARIATION(productId), {
        params: {
          page_size: pageSize,
          ...(diamondId && { center_stone_id: diamondId }),
          price_mode: priceMode,
          ...dynamicParams,
        },
        paramsSerializer: customParamsSerializer,
        headers: {
          ...apiClient.defaults.headers.common,
          "If-None-Match": "",
        },
      }),
  });
};

export const useSimilarDiamondData = (
  diamiond_ids: number,
  userInfo: any,
  priceMode: number,
  priceMarkup: string,
  isSwitchFromStock?: boolean,
  switch_from_stock_id?: number,
  isLabgrown?: boolean,
  isEnabled?: boolean,
  mGroupIds?: any
) =>
  useQuery({
    enabled: isEnabled,
    queryKey: [
      "SearchSimilarDiamond",
      diamiond_ids,
      userInfo,
      priceMode,
      priceMarkup,
      isSwitchFromStock,
      switch_from_stock_id,
      userInfo.user.currency,
    ],
    queryFn: () =>
      apiClient.get<DiamondDetail>(Routes.GET_SIMILAR_DIAMOND(diamiond_ids), {
        params: {
          price_mode: priceMode,
          "vdb[lab_grown]": isLabgrown,
          ...(mGroupIds && mGroupIds.length > 0 && mGroupIds[0] !== 0
            ? { "vdb[group_ids]": mGroupIds }
            : {}),
          ...(isSwitchFromStock && switch_from_stock_id
            ? { switch_from_stock_id: switch_from_stock_id }
            : {}),
        },
        headers: {
          ...apiClient.defaults.headers.common,
          "If-None-Match": "",
        },
      }),
  });

export type MatchingBandDataParams = {
  productId: number | string;
  matchGroupId: string;
};

export const useMatchingBandData = () => {
  return useMutation((params: MatchingBandDataParams) => {
    if (!params?.productId || !params?.matchGroupId) {
      // Keep errors generic (avoid leaking request details).
      return Promise.reject(
        new Error("Missing required parameters for matching band request")
      );
    }

    return apiClient.get<MatchingBandDataResponse>(
      Routes.GET_MATCHING_BAND_DATA(params.productId),
      {
        params: {
          match_group_id: params.matchGroupId,
        },
        headers: {
          ...apiClient.defaults.headers.common,
          "If-None-Match": "",
        },
      }
    );
  });
};

export const useSimilarDiamondDataWithMatch = () => {
  return useMutation(
    (params: {
      diamond_ids: number;
      price_mode: number;
      switch_from_stock_id?: number;
      isLabgrown?: boolean;
    }) =>
      apiClient.get<DiamondDetail>(
        Routes.GET_SIMILAR_DIAMOND(params.diamond_ids),
        {
          params: {
            price_mode: params.price_mode,
            switch_from_stock_id: params.switch_from_stock_id,
            "vdb[lab_grown]": params.isLabgrown,
          },
          headers: {
            ...apiClient.defaults.headers.common,
            "If-None-Match": "",
          },
        }
      )
  );
};

export const useGIARequest = (
  productId: number,
  isEnabled?: boolean,
  certNum?: string,
  isLinkedPair?: boolean
) =>
  useQuery({
    enabled: isEnabled,
    queryKey: ["giaCertificate", productId, certNum],
    queryFn: () =>
      apiClient.get<GIACertificateResponse>(
        Routes.GET_GIA_CERTIFICATE(productId),
        {
          ...(isLinkedPair && certNum
            ? { params: { is_linked_pair: true, cert_num: certNum } }
            : {}),
          headers: {
            ...apiClient.defaults.headers.common,
            "If-None-Match": "",
          },
        }
      ),
  });

export const useAddToJewelryOptions = (
  stockItemIds: number[],
  required: boolean,
  isEnabled: boolean
) => {
  return useQuery({
    enabled: isEnabled,
    queryKey: ["getAddToJewelryOptions", stockItemIds, required],
    queryFn: () =>
      apiClient
        .get<AddToJewelryResponse>(Routes.GET_ADD_TO_JEWELRY_OPTIONS, {
          params: {
            "stock_item_ids[]": stockItemIds,
            required: required,
          },
          headers: {
            ...apiClient.defaults.headers.common,
            "If-None-Match": "",
          },
        })
        .then((res) => res.data),
  });
};

export const useReportBadMedia = () => {
  return useMutation({
    mutationFn: (data: { media_id: number }) =>
      apiClient.post(Routes.REPORT_BAD_MEDIA, data),
  });
};
